"use client";
import { useMemo } from "react";

// Ui components
import { Button } from '@/components/ui/button';
import { Spinner } from "@/components/ui/spinner";
import { ChevronDown } from "lucide-react";
// custom components
import { CompanyCard } from "@/components/pages/company/CompanyCard";
import { BannerCarousel } from "@/components/reusebale-components/bannerCarousel";
import { CallToAction } from "@/components/reusebale-components/CallToAction";
import HeaderTitle from "@/components/reusebale-components/HeaderTitle";
import { Separator } from "@/components/ui/separator";

//
import endPoints from "@/lib/apiEndPoints";
import { useFetchSWR, useFetchSWRInfinite } from "@/lib/swrConfig";
import { bannerEnum as be } from "@/lib/utils";
import { BannerData } from "@/types/banner";

const QUERY_LIMIT = 8;

export default function OurCompanies() {
  // banner data
  const { data: bannerData, isLoading } = useFetchSWR(endPoints.BANNER(be.COMPANY));
  const banner: BannerData = bannerData?.status === 200 ? bannerData.data : {};

  // companies data
const getKey = (pageIndex: number, previousPageData: any) => {
  if (previousPageData && previousPageData.data?.length === 0) return null; 
  return endPoints.COMPANIES(pageIndex + 1, QUERY_LIMIT,);
};


const { data, size, setSize, isValidating } = useFetchSWRInfinite(getKey);

const companies = data
  ? Array.from(
      new Map(
        data.flatMap((page) => page.data).map((c) => [c.id, c])
      ).values()
    )
  : [];

const isReachingEnd = data && data[data.length - 1]?.data.length < QUERY_LIMIT;

  // Memoized sorting
  const { recentlyAdded, sisterCompanies } = useMemo(() => {
    const sorted = [...companies].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return { recentlyAdded: sorted.slice(0, 2), sisterCompanies: sorted.slice(2) };
  }, [companies]);

  return (
    <>
      {/* Banner Carousel */}
      {
        isLoading ? (
            <div className="h-screen flex items-center justify-center">
            <Spinner />
            </div>
        ) : (
            <BannerCarousel
            images={banner?.media?.map((media) => media.url)}
            title={banner?.title}
            description={banner?.details}
            />
        )
      }

      <div className="container ">
        {
          recentlyAdded.length > 0 && (
           <section>
            {/* --- Recently Added Section --- */}
            <div className="text-center mt-6 md:mt-10 lg:mt-16">
              <HeaderTitle title="Recently Added" url="" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ">
                {recentlyAdded.map((company, index) => (
                  <CompanyCard key={index} company={company} />
                ))}
              </div>
            </div>
          </section>
          )
        }

        <Separator className="my-6 md:my-10 lg:my-16 " />

        {/* --- All Sister Companies Section --- */}
        <div>
          <HeaderTitle title="Explore Our All Sister Companies" url="" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
            {sisterCompanies.map((company) => (
              <CompanyCard key={company.id} company={company} />
            ))}
          </div>

          {/* --- See More / See Less Button --- */}
          <div className="mt-8 flex justify-center">
            {!isReachingEnd ? (
              <Button
                onClick={() => setSize(size + 1)}
                disabled={isValidating}
                className="flex items-center bg-primary text-white rounded-full px-8 py-3 cursor-pointer hover:bg-prime-hover transition-colors"
                size="lg"
              >
                {isValidating ? <Spinner /> : "See More"}
                <ChevronDown size={16} className="ml-1" />
              </Button>
            ) : size > 1 ? (
              <Button
                onClick={() => setSize(1)}
                disabled={isValidating}
                className="flex items-center bg-gray-600 text-white rounded-full px-8 py-3 cursor-pointer hover:bg-gray-700 transition-colors"
                size="lg"
              >
                {isValidating ? <Spinner /> : "See Less"}
              </Button>
            ) : null}
          </div>
        </div>

        <Separator className="my-6 md:my-10 lg:my-16 " />

        <div className="mb-6 md:mb-10 lg:mb-16">
          <CallToAction
            title="Join Us in Making a Difference"
            description="Your support can create lasting change in our communities. Get involved and make an impact today!"
            buttonText="Contact"
          />
        </div>
      </div>
    </>
  );
}
